import { MGPFallible, MGPOptional, MGPValidation } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { GameNode } from '../../jscaip/AI/GameNode';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { GameStatus } from '../../jscaip/GameStatus';
import { Player } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { MGPValidators } from '../../utils/MGPValidator';
import { AbaloneFailure } from './AbaloneFailure';
import { AbaloneState } from './AbaloneState';
export class AbaloneNode extends GameNode {
}
export class AbaloneRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Abalone`,
        config: {
            nbToCapture: new NumberConfig(6, () => $localize `Number of pieces to capture in order to win`, MGPValidators.range(1, 14)),
            maximumPushingGroupSize: new NumberConfig(3, () => $localize `Maximum pushing group size`, MGPValidators.range(1, 9)),
        },
    }, [
        {
            name: () => $localize `Deadly Abalone`,
            config: {
                nbToCapture: 1,
                maximumPushingGroupSize: 9,
            },
        },
    ]);
    static get() {
        if (AbaloneRules.singleton.isAbsent()) {
            AbaloneRules.singleton = MGPOptional.of(new AbaloneRules());
        }
        return AbaloneRules.singleton.get();
    }
    getRulesConfigDescription() {
        return AbaloneRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState() {
        const _ = FourStatePiece.EMPTY;
        const N = FourStatePiece.UNREACHABLE;
        const O = FourStatePiece.ZERO;
        const X = FourStatePiece.ONE;
        const board = [
            [N, N, N, N, X, X, X, X, X],
            [N, N, N, X, X, X, X, X, X],
            [N, N, _, _, X, X, X, _, _],
            [N, _, _, _, _, _, _, _, _],
            [_, _, _, _, _, _, _, _, _],
            [_, _, _, _, _, _, _, _, N],
            [_, _, O, O, O, _, _, N, N],
            [O, O, O, O, O, O, N, N, N],
            [O, O, O, O, O, N, N, N, N],
        ];
        return new AbaloneState(board, 0);
    }
    static isLegalRealPush(firstOpponent, move, state, pushingPieces, newBoard) {
        let opponentPieces = 0;
        const opponent = FourStatePiece.ofPlayer(state.getCurrentOpponent());
        const player = FourStatePiece.ofPlayer(state.getCurrentPlayer());
        while (opponentPieces < pushingPieces &&
            state.hasPieceAt(firstOpponent, opponent)) {
            opponentPieces++;
            firstOpponent = firstOpponent.getNext(move.dir);
        }
        if (pushingPieces <= opponentPieces) {
            return MGPFallible.failure(AbaloneFailure.NOT_ENOUGH_PIECE_TO_PUSH());
        }
        else if (state.hasPieceAt(firstOpponent, FourStatePiece.EMPTY)) {
            newBoard[firstOpponent.y][firstOpponent.x] = opponent;
        }
        else if (state.hasPieceAt(firstOpponent, player)) {
            return MGPFallible.failure(AbaloneFailure.CANNOT_PUSH_YOUR_OWN_PIECES());
        }
        return MGPFallible.success(newBoard);
    }
    applyLegalMove(_move, state, _config, newBoard) {
        return new AbaloneState(newBoard, state.turn + 1);
    }
    isLegal(move, state, config) {
        const firstPieceValidity = this.getFirstPieceValidity(move, state);
        if (firstPieceValidity.isFailure()) {
            return firstPieceValidity.toOtherFallible();
        }
        if (move.isSingleCoord()) {
            return this.isLegalPush(move, state, config);
        }
        else {
            return this.isLegalSideStep(move, state);
        }
    }
    getFirstPieceValidity(move, state) {
        const firstPiece = state.getPieceAt(move.coord);
        if (firstPiece.isPlayer() === false) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        else if (firstPiece === FourStatePiece.ofPlayer(state.getCurrentOpponent())) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        else {
            return MGPValidation.SUCCESS;
        }
    }
    isLegalPush(move, state, config) {
        let pieces = 1;
        let tested = move.coord.getNext(move.dir);
        const player = FourStatePiece.ofPlayer(state.getCurrentPlayer());
        const empty = FourStatePiece.EMPTY;
        const newBoard = state.getCopiedBoard();
        newBoard[move.coord.y][move.coord.x] = empty;
        while (pieces <= config.maximumPushingGroupSize &&
            state.hasPieceAt(tested, player)) {
            pieces++;
            tested = tested.getNext(move.dir);
        }
        if (pieces > config.maximumPushingGroupSize) {
            return MGPFallible.failure(AbaloneFailure.CANNOT_MOVE_MORE_THAN_N_PIECES(config.maximumPushingGroupSize));
        }
        else if (state.isNotOnBoard(tested)) {
            return MGPFallible.success(newBoard);
        }
        newBoard[tested.y][tested.x] = player;
        if (state.getPieceAt(tested) === empty) {
            return MGPFallible.success(newBoard);
        }
        return AbaloneRules.isLegalRealPush(tested, move, state, pieces, newBoard);
    }
    isLegalSideStep(move, state) {
        let last = move.lastPiece.get();
        const alignment = move.coord.getDirectionToward(last).get();
        last = last.getNext(alignment); // to include lastPiece as well
        let tested = move.coord;
        const player = FourStatePiece.ofPlayer(state.getCurrentPlayer());
        const newBoard = state.getCopiedBoard();
        while (tested.equals(last) === false && state.isOnBoard(tested)) {
            if (state.getPieceAt(tested) !== player) {
                return MGPFallible.failure(AbaloneFailure.MUST_ONLY_TRANSLATE_YOUR_PIECES());
            }
            const landing = tested.getNext(move.dir);
            newBoard[tested.y][tested.x] = FourStatePiece.EMPTY;
            if (state.isOnBoard(landing)) {
                if (state.isPlayerAt(landing)) {
                    return MGPFallible.failure(AbaloneFailure.TRANSLATION_IMPOSSIBLE());
                }
                if (state.getPieceAt(landing) === FourStatePiece.EMPTY) {
                    newBoard[landing.y][landing.x] = player;
                }
            }
            tested = tested.getNext(alignment);
        }
        return MGPFallible.success(newBoard);
    }
    getGameStatus(node, config) {
        const scores = node.gameState.getScores();
        const nbToCapture = config.nbToCapture;
        if (nbToCapture <= scores.get(Player.ZERO)) {
            return GameStatus.ZERO_WON;
        }
        else if (nbToCapture <= scores.get(Player.ONE)) {
            return GameStatus.ONE_WON;
        }
        else {
            return GameStatus.ONGOING;
        }
    }
}
